import React from 'react'
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material'
import Grid from '@mui/material/Grid2'
import Link from '@mui/material/Link';
import { RightArrow } from '../assets/icons';
import { Logo } from '../assets/images';
import { useState } from 'react';
import AuthService from '../services/auth.service';
import { useNavigate } from 'react-router-dom';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0); // 0 for email, 1 for verification
    const [email, setEmail] = useState('');
    const [formData, setFormData] = useState({
        code: '',
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [codeError, setCodeError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const validatePassword = (password: string) => {
        const minLength = 8;
        const hasNumber = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        if (password.length < minLength) {
            return 'Password must be at least 8 characters long';
        }
        if (!hasNumber) {
            return 'Password must contain at least one number';
        }
        if (!hasSpecialChar) {
            return 'Password must contain at least one special character';
        }
        return '';
    };

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await AuthService.forgotPassword(email);
            setStep(1); // Move to verification step
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerificationSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setCodeError('');
        setError('');

        const passwordValidationError = validatePassword(formData.password);
        if (passwordValidationError) {
            setError(passwordValidationError);
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);
        try {
            await AuthService.confirmForgotPassword(
                email,
                formData.code,
                formData.password
            );
            window.location.href = '/login';
        } catch (err: any) {
            setCodeError(err.message);
            setIsLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Add password validation on input change
        if (name === 'password') {
            setPasswordError(validatePassword(value));
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        // Check password match on blur for confirm password field
        if (name === 'confirmPassword') {
            if (value !== formData.password) {
                setError('Passwords do not match');
            } else {
                setError('');
            }
        }
    };

    return (
        <>
            <Box p={{ xs: "16px 16px 11px", sm: "18px 20px 16px", md: "24px 20px 16px" }} sx={{ background: "#F5F6F8" }} display="flex" justifyContent="space-between" alignItems="center" mt={{ xs: "", sm: "unset" }} position="sticky" top="0" zIndex="2">
                <Box display="flex"><img src={Logo} alt="logo" /></Box>
                <Button
                    variant="text"
                    onClick={() => navigate('/login')}
                    sx={{
                        textTransform: "capitalize",
                        color: "#111827 !important",
                        fontSize: "min(calc(14px + 4 * (100vw - 400px) / (1920 - 400)), 16px)",
                        display: "flex",
                        gap: "8px",
                        "&:hover": {
                            boxShadow: "none!important",
                            borderRadius: "8px",
                            "& svg": {
                                transform: "translateX(2px)",
                                transition: "all 0.3s ease",
                            }
                        }
                    }}
                >
                    Log in
                    <RightArrow />
                </Button>
            </Box>

            {step === 0 ? (
                // Email Step
                <Box p={{ xs: "16px", sm: "20px", xl: "0 48px 0" }} sx={{ background: "#F5F6F8", backgroundSize: "cover", backgroundPosition: "center" }} display="flex" alignItems="center" justifyContent="center" minHeight={{ xs: "calc(100vh - 63px)", sm: "calc(100vh - 80px)" }}>
                    <Box maxWidth="576px" width="100%">
                        <Box p={{ xs: "20px", sm: "40px" }} display="flex" flexDirection="column" justifyContent="center" alignItems="center" boxShadow="0px 4px 8px 0px #0000000D" border="1px solid #E9E9E9" bgcolor="#FFFFFF" margin="auto" borderRadius="24px" zIndex={{ sm: "2" }} position={{ md: "relative" }}>
                            <>
                                <Typography
                                    color="#060023"
                                    fontWeight="700"
                                    fontSize={{ xs: "min(calc(18px + 10 * (100vw - 400px) / (1920 - 400)), 24px)", sm: "24px" }}
                                >
                                    Reset your password?
                                </Typography>
                                <Typography variant="body1" color="#666666" mb="8px" mt="8px" fontSize={{ xs: "min(calc(14px + 4 * (100vw - 400px) / (1920 - 400)), 14px)" }} textAlign="center">Enter the email to reset your password</Typography>
                            </>

                            <Box component="form" onSubmit={handleEmailSubmit} sx={{ width: '100%', marginTop: "15px" }}>
                                <Grid container spacing={3} mb="38px">
                                    <Grid size={{ xs: 12 }}>
                                        <TextField
                                            variant="outlined"
                                            label="Email address"
                                            name="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            error={!!error}
                                            helperText={error}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: '3px',
                                                    borderColor: '#666666',
                                                },

                                                " .MuiInputLabel-root.Mui-focused": {
                                                    color: "inherit",
                                                },

                                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: '#333333',
                                                    border: '1px solid'
                                                },

                                                "& .MuiOutlinedInput-input": {
                                                    padding: '14px 16px',
                                                    background: '#ffffff',
                                                    borderRadius: '8px'
                                                },
                                                "&.MuiInputBase-input::placeholder": {
                                                    color: '#333333',
                                                    fontSize: '16px',
                                                    opacity: '0.8'
                                                },
                                            }}
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>

                                <Box margin="auto">
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        disabled={isLoading}
                                        disableElevation
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textTransform: "capitalize",
                                            width: "100%",
                                            height: "51px",
                                            gap: "8px",
                                            fontSize: "16px",
                                            fontWeight: "500",
                                            bgcolor: "#0382F7 !important",
                                            "&:hover": { bgcolor: "#006dd1 !important" },
                                            "&:disabled": { opacity: 0.7 }
                                        }}
                                    >
                                        {isLoading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
                                    </Button>
                                </Box>

                                <Box className="login-authenticator-wrapper">
                                    <Box
                                        className="ew-copyright"
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: { xs: "20px", md: "unset" },
                                            justifyContent: { xs: "center" },
                                            alignItems: 'center',
                                            padding: '0 !important',
                                            position: 'unset!important',
                                            margin: '36px 0 0 !important',
                                        }}
                                    >
                                        <Typography
                                            component="span"
                                            sx={{ paddingRight: "13px !important", fontWeight: "500", color: "#667085 !important" }}
                                            borderRight={{ xs: "none !Important", sm: "1px solid #c5c5c5 !Important" }}
                                        >
                                            &copy; 2025 All Rights Reserved
                                        </Typography>
                                        <Box
                                            component="ul"
                                            sx={{
                                                display: 'flex',
                                                listStyle: 'none',
                                                margin: 0,
                                                padding: 0,
                                                gap: '4px',
                                                position: 'relative',
                                                flexWrap: 'wrap',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Box component="li">
                                                <Link
                                                    href="https://easywebinar.com/terms-conditions/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className='terms'
                                                    sx={{
                                                        textDecoration: 'none',
                                                        color: "#667085 !important",
                                                        fontSize: '14px',
                                                        fontWeight: "500",
                                                        marginRight: "4px",
                                                        '&:hover': {
                                                            textDecoration: 'underline',
                                                        },
                                                    }}
                                                >
                                                    Terms of Service
                                                </Link>
                                            </Box>
                                            <Box component="li">
                                                <Link
                                                    href="https://easywebinar.com/privacy/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        textDecoration: 'none',
                                                        color: "#667085 !important",
                                                        fontSize: '14px',
                                                        fontWeight: "500",
                                                        marginRight: "4px",
                                                        '&:hover': {
                                                            textDecoration: 'underline',
                                                        },
                                                    }}
                                                >
                                                    Privacy Policy
                                                </Link>
                                            </Box>
                                            <Box component="li">
                                                <Link
                                                    href="https://easywebinar.com/cookies/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        textDecoration: 'none',
                                                        fontWeight: "500",
                                                        color: "#667085 !important",
                                                        '&:hover': {
                                                            textDecoration: 'underline',
                                                        },
                                                    }}
                                                >
                                                    Security
                                                </Link>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            ) : (
                // Verification Step
                <Box p={{ xs: "16px", sm: "20px", xl: "0 48px 0" }} sx={{ background: "#F5F6F8", backgroundSize: "cover", backgroundPosition: "center" }} display="flex" alignItems="center" justifyContent="center" minHeight={{ xs: "calc(100vh - 63px)", sm: "calc(100vh - 80px)" }}>
                    <Box maxWidth="576px" width="100%">
                        <Box p={{ xs: "20px", sm: "40px" }} display="flex" flexDirection="column" justifyContent="center" alignItems="center" boxShadow="0px 4px 8px 0px #0000000D" border="1px solid #E9E9E9" bgcolor="#FFFFFF" margin="auto" borderRadius="24px" zIndex={{ sm: "2" }} position={{ md: "relative" }}>
                            <>
                                <Typography
                                    color="#060023"
                                    fontWeight="700"
                                    fontSize={{ xs: "min(calc(18px + 10 * (100vw - 400px) / (1920 - 400)), 24px)", sm: "24px" }}
                                >
                                    Enter verification code
                                </Typography>
                                <Typography variant="body1" color="#666666" mb="8px" mt="8px" fontSize={{ xs: "min(calc(14px + 4 * (100vw - 400px) / (1920 - 400)), 14px)" }} textAlign="center">Enter the 6-digit code you have received on your email</Typography>
                            </>

                            <Box component="form" onSubmit={handleVerificationSubmit} sx={{ width: '100%', marginTop: "15px" }}>
                                <Grid container spacing={3} mb="38px">
                                    <Grid size={{ xs: 12 }}>
                                        <TextField
                                            variant="outlined"
                                            label="Enter Code"
                                            name="code"
                                            value={formData.code}
                                            onChange={handleInputChange}
                                            error={!!codeError}
                                            helperText={codeError}
                                            autoComplete="off"
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: '3px',
                                                    borderColor: '#666666',
                                                },

                                                " .MuiInputLabel-root.Mui-focused": {
                                                    color: "inherit",
                                                },

                                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: '#333333',
                                                    border: '1px solid'
                                                },

                                                "& .MuiOutlinedInput-input": {
                                                    padding: '14px 16px',
                                                    background: '#ffffff',
                                                    borderRadius: '8px'
                                                },
                                                "&.MuiInputBase-input::placeholder": {
                                                    color: '#333333',
                                                    fontSize: '16px',
                                                    opacity: '0.8'
                                                },
                                            }}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12 }}>
                                        <TextField
                                            variant="outlined"
                                            label="Create Password"
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            error={!!passwordError}
                                            helperText={passwordError}
                                            autoComplete="new-password"
                                            InputProps={{
                                                endAdornment: (
                                                    <Box component="span" sx={{ cursor: 'pointer' }} display="flex" onClick={() => setShowPassword(!showPassword)}>
                                                        {showPassword ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                                                <line x1="1" y1="1" x2="23" y2="23"></line>
                                                            </svg>
                                                        ) : (
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                                <circle cx="12" cy="12" r="3"></circle>
                                                            </svg>
                                                        )}
                                                    </Box>
                                                )
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: '3px',
                                                    borderColor: '#666666',
                                                },

                                                " .MuiInputLabel-root.Mui-focused": {
                                                    color: "inherit",
                                                },

                                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: '#333333',
                                                    border: '1px solid'
                                                },

                                                "& .MuiOutlinedInput-input": {
                                                    padding: '14px 16px',
                                                    background: '#ffffff',
                                                    borderRadius: '8px'
                                                },
                                                "&.MuiInputBase-input::placeholder": {
                                                    color: '#333333',
                                                    fontSize: '16px',
                                                    opacity: '0.8'
                                                },
                                            }}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 12 }}>
                                        <TextField
                                            variant="outlined"
                                            label="Re-Enter Password"
                                            name="confirmPassword"
                                            type={showConfirmPassword ? "text" : "password"}
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            onBlur={handleBlur}
                                            error={!!error && formData.confirmPassword !== ''}
                                            helperText={formData.confirmPassword !== '' ? error : ''}
                                            autoComplete="new-password"
                                            InputProps={{
                                                endAdornment: (
                                                    <Box component="span" sx={{ cursor: 'pointer' }} display="flex" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                                        {showConfirmPassword ? (
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                                                <line x1="1" y1="1" x2="23" y2="23"></line>
                                                            </svg>
                                                        ) : (
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                                                <circle cx="12" cy="12" r="3"></circle>
                                                            </svg>
                                                        )}
                                                    </Box>
                                                )
                                            }}
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: '3px',
                                                    borderColor: '#666666',
                                                },

                                                " .MuiInputLabel-root.Mui-focused": {
                                                    color: "inherit",
                                                },

                                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: '#333333',
                                                    border: '1px solid'
                                                },

                                                "& .MuiOutlinedInput-input": {
                                                    padding: '14px 16px',
                                                    background: '#ffffff',
                                                    borderRadius: '8px'
                                                },
                                                "&.MuiInputBase-input::placeholder": {
                                                    color: '#333333',
                                                    fontSize: '16px',
                                                    opacity: '0.8'
                                                },
                                            }}
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>

                                <Box marginTop="25px" mb="24px">
                                    <Typography variant="body2" color="#060023" display="inline">
                                        Didn't receive the code?
                                    </Typography>
                                    <Button
                                        variant="text"
                                        color="error"
                                        size='small'
                                        onClick={() => AuthService.forgotPassword(email)}
                                        sx={{ textTransform: "capitalize", fontWeight: "normal", fontSize: '14px', ml: "8px" }}
                                    >
                                        Resend Code
                                    </Button>
                                </Box>

                                <Box margin="auto">
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        disabled={isLoading}
                                        disableElevation
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textTransform: "capitalize",
                                            width: "100%",
                                            height: "51px",
                                            gap: "8px",
                                            fontSize: "16px",
                                            fontWeight: "500",
                                            bgcolor: "#0382F7 !important",
                                            "&:hover": { bgcolor: "#006dd1 !important" },
                                            "&:disabled": { opacity: 0.7 }
                                        }}
                                    >
                                        {isLoading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
                                    </Button>
                                </Box>

                                <Box className="login-authenticator-wrapper">
                                    <Box
                                        className="ew-copyright"
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: { xs: "20px", md: "unset" },
                                            justifyContent: { xs: "center" },
                                            alignItems: 'center',
                                            padding: '0 !important',
                                            position: 'unset!important',
                                            margin: '36px 0 0 !important',
                                        }}
                                    >
                                        <Typography
                                            component="span"
                                            sx={{ paddingRight: "13px !important", fontWeight: "500", color: "#667085 !important" }}
                                            borderRight={{ xs: "none !Important", sm: "1px solid #c5c5c5 !Important" }}
                                        >
                                            &copy; 2025 All Rights Reserved
                                        </Typography>
                                        <Box
                                            component="ul"
                                            sx={{
                                                display: 'flex',
                                                listStyle: 'none',
                                                margin: 0,
                                                padding: 0,
                                                gap: '4px',
                                                position: 'relative',
                                                flexWrap: 'wrap',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Box component="li">
                                                <Link
                                                    href="https://easywebinar.com/terms-conditions/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className='terms'
                                                    sx={{
                                                        textDecoration: 'none',
                                                        color: "#667085 !important",
                                                        fontSize: '14px',
                                                        fontWeight: "500",
                                                        marginRight: "4px",
                                                        '&:hover': {
                                                            textDecoration: 'underline',
                                                        },
                                                    }}
                                                >
                                                    Terms of Service
                                                </Link>
                                            </Box>
                                            <Box component="li">
                                                <Link
                                                    href="https://easywebinar.com/privacy/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        textDecoration: 'none',
                                                        color: "#667085 !important",
                                                        fontSize: '14px',
                                                        fontWeight: "500",
                                                        marginRight: "4px",
                                                        '&:hover': {
                                                            textDecoration: 'underline',
                                                        },
                                                    }}
                                                >
                                                    Privacy Policy
                                                </Link>
                                            </Box>
                                            <Box component="li">
                                                <Link
                                                    href="https://easywebinar.com/cookies/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        textDecoration: 'none',
                                                        fontWeight: "500",
                                                        color: "#667085 !important",
                                                        '&:hover': {
                                                            textDecoration: 'underline',
                                                        },
                                                    }}
                                                >
                                                    Security
                                                </Link>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}


        </>
    )
}

export default ForgotPassword